/**
 * AdminAdsPage - aprovar / rejeitar / pausar anúncios B2B do Marketplace.
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Eye, Check, X, Loader2, Pause, Play, ExternalLink, Building2 } from "lucide-react";
import { api, getApiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import StatusBadge from "@/components/shared/StatusBadge";
import { fmtDateTime } from "@/lib/date";

function adStatusKey(s) {
    if (s === "em_revisao") return "pendente";
    if (s === "aprovado" || s === "ativo") return "aprovado";
    if (s === "rejeitado") return "rejeitado";
    if (s === "pausado") return "pausada";
    if (s === "expirado") return "concluida";
    return "rascunho";
}

export default function AdminAdsPage() {
    const [tab, setTab] = useState("em_revisao");
    const [items, setItems] = useState([]);
    const [sel, setSel] = useState(null);
    const [note, setNote] = useState("");
    const [working, setWorking] = useState(false);

    const load = async () => {
        const status = tab === "all" ? "" : tab;
        const { data } = await api.get(`/ads/admin/list${status ? `?status=${status}` : ""}`);
        setItems(data);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [tab]);

    const review = async (decision) => {
        if (!sel) return;
        setWorking(true);
        try {
            await api.post(`/ads/admin/${sel.id}/review`, { decision, note: note || null });
            toast.success(decision === "aprovado" ? "Anúncio aprovado e ativo no Marketplace" : "Anúncio rejeitado");
            setSel(null);
            setNote("");
            load();
        } catch (e) { toast.error(getApiErrorMessage(e)); }
        finally { setWorking(false); }
    };

    const toggle = async (a) => {
        try {
            await api.post(`/ads/admin/${a.id}/toggle`);
            toast.success(a.status === "aprovado" ? "Anúncio pausado" : "Anúncio reactivado");
            load();
        } catch (e) { toast.error(getApiErrorMessage(e)); }
    };

    return (
        <div className="space-y-6" data-testid="admin-ads-page">
            <div>
                <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Marketplace · Anúncios B2B</h1>
                <p className="text-muted-foreground">Aprovar, rejeitar e gerir anúncios submetidos por subscritores Profissional+.</p>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="em_revisao" data-testid="admin-ads-tab-pending">Em revisão</TabsTrigger>
                    <TabsTrigger value="aprovado" data-testid="admin-ads-tab-approved">Aprovados</TabsTrigger>
                    <TabsTrigger value="rejeitado" data-testid="admin-ads-tab-rejected">Rejeitados</TabsTrigger>
                    <TabsTrigger value="pausado" data-testid="admin-ads-tab-paused">Pausados</TabsTrigger>
                    <TabsTrigger value="all" data-testid="admin-ads-tab-all">Todos</TabsTrigger>
                </TabsList>
                <TabsContent value={tab} className="mt-4">
                    {items.length === 0 ? (
                        <Card className="p-10 text-center text-muted-foreground">Sem anúncios.</Card>
                    ) : (
                        <div className="grid lg:grid-cols-2 gap-4">
                            {items.map((a) => (
                                <Card key={a.id} className="glass-card p-0 rounded-2xl border-white/10 overflow-hidden" data-testid={`admin-ad-${a.id}`}>
                                    {a.image_url && (
                                        <div className="aspect-video bg-black/30 overflow-hidden">
                                            <img src={a.image_url} alt="" className="w-full h-full object-cover" />
                                        </div>
                                    )}
                                    <div className="p-4 space-y-2">
                                        <div className="flex items-start justify-between gap-3 flex-wrap">
                                            <div className="min-w-0">
                                                <h3 className="font-display font-semibold">{a.title}</h3>
                                                <p className="text-sm text-muted-foreground line-clamp-1">{a.headline}</p>
                                            </div>
                                            <StatusBadge status={adStatusKey(a.status)} />
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                                            <Building2 className="h-3 w-3" /> {a.advertiser_company || a.advertiser_name}
                                            {a.industry && <Badge variant="outline" className="text-[10px]">{a.industry}</Badge>}
                                        </div>
                                        <div className="text-xs text-muted-foreground">{fmtDateTime(a.created_at)}</div>
                                        <div className="flex gap-2 pt-2 border-t border-white/5">
                                            <Button size="sm" variant="outline" onClick={() => setSel(a)} data-testid={`admin-ad-view-${a.id}`}>
                                                <Eye className="h-3.5 w-3.5" />
                                            </Button>
                                            {a.status === "aprovado" && (
                                                <Button size="sm" variant="outline" onClick={() => toggle(a)} data-testid={`admin-ad-pause-${a.id}`}>
                                                    <Pause className="h-3.5 w-3.5" />
                                                </Button>
                                            )}
                                            {a.status === "pausado" && (
                                                <Button size="sm" variant="outline" onClick={() => toggle(a)} data-testid={`admin-ad-resume-${a.id}`}>
                                                    <Play className="h-3.5 w-3.5" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
                <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{sel?.title}</DialogTitle>
                        <DialogDescription>
                            Por {sel?.advertiser_company || sel?.advertiser_name} · {sel && fmtDateTime(sel.created_at)}
                        </DialogDescription>
                    </DialogHeader>
                    {sel && (
                        <div className="space-y-3">
                            {sel.image_url && (
                                <img src={sel.image_url} alt="" className="w-full aspect-video object-cover rounded-xl" />
                            )}
                            <p className="text-sm font-medium">{sel.headline}</p>
                            {sel.description && <p className="text-sm text-muted-foreground whitespace-pre-line">{sel.description}</p>}
                            <div className="grid grid-cols-2 gap-2 text-xs">
                                <div><span className="text-muted-foreground">Setor:</span> {sel.industry || "—"}</div>
                                <div><span className="text-muted-foreground">CTA:</span> {sel.cta_label}</div>
                                <div><span className="text-muted-foreground">Orçamento:</span> {sel.budget_aoa.toLocaleString("pt-PT")} AOA</div>
                                <div><span className="text-muted-foreground">Duração:</span> {sel.duration_days} dias</div>
                                {sel.target_audience && <div className="col-span-2"><span className="text-muted-foreground">Audiência:</span> {sel.target_audience}</div>}
                            </div>
                            <a href={sel.target_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-[#A78BFA] hover:underline">
                                Ver link de destino <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                            {sel.status === "em_revisao" && (
                                <div>
                                    <Label>Nota (opcional, vista pelo anunciante)</Label>
                                    <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-ad-note-input" />
                                </div>
                            )}
                            {sel.admin_note && sel.status !== "em_revisao" && (
                                <Card className="p-3 rounded-xl bg-secondary/40 text-sm">
                                    <span className="text-muted-foreground">Nota admin:</span> {sel.admin_note}
                                </Card>
                            )}
                            <div className="flex gap-3 text-xs text-muted-foreground border-t border-white/5 pt-2">
                                <span>{(sel.impressions || 0).toLocaleString("pt-PT")} impressões</span>
                                <span>{(sel.clicks || 0).toLocaleString("pt-PT")} cliques</span>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSel(null)}>Fechar</Button>
                        {sel?.status === "em_revisao" && (
                            <>
                                <Button variant="outline" className="border-[#FB7185]/40 text-[#FB7185]" onClick={() => review("rejeitado")} disabled={working} data-testid="admin-ad-reject-button">
                                    <X className="h-4 w-4 mr-2" /> Rejeitar
                                </Button>
                                <Button onClick={() => review("aprovado")} disabled={working} className="btn-grad-purple text-white" data-testid="admin-ad-approve-button">
                                    {working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                                    Aprovar
                                </Button>
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
